<template>
  <ak-sidebar-layout size="large">
    <template #sidebar>
      <project-sidebar
        v-model:active-filter="projectIdFilter"
        :account-id="activeAccount._id"
        :agency-id="activeAgency._id"
        :user-profile="userProfile"
      />
    </template>
    <template #body>
      <div class="relative h-full">
        <ak-basic-container class="flex flex--column pt-small px pb-huge">
          <h4 class="color-grey">Page Manager</h4>
          <ak-form>
            <ak-pack>
              <ak-pack-item>
                <ak-field>
                  <ak-input
                    v-model="searchFilter"
                    type="search"
                    icon="search"
                    placeholder="Search Containers"
                  />
                </ak-field>
              </ak-pack-item>
              <ak-pack-item grow />
            </ak-pack>
          </ak-form>
          <ak-loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="false"
            :backdrop="false"
          />
          <ak-box
            v-if="!isLoading"
            background="white"
            class="p-none has-shadow"
          >
            <ak-display-table hover rows cosy>
              <ak-display-table-row
                v-for="(item, index) in filteredContainers"
                :key="index"
                pointer
              >
                <ak-display-table-cell @click.stop.prevent="editModal(item)">
                  <h4 class="text-grey m-none">
                    {{ item.title }}
                  </h4>
                </ak-display-table-cell>
                <ak-display-table-cell @click.stop.prevent="editModal(item)">
                  <em>
                    <small> Edited {{ fromNow(item.updatedOn) }} </small>
                  </em>
                </ak-display-table-cell>
                <ak-display-table-cell class="width-125">
                  <ak-button color="grey-lighter" @click="installModal(item)">
                    Install
                    <ak-icon type="open" class="ml-tiny" />
                  </ak-button>
                </ak-display-table-cell>
                <ak-display-table-cell class="width-50">
                  <ak-dropdown
                    position-x="right"
                    position-y="top"
                    class="pull-right"
                  >
                    <template #trigger>
                      <ak-button circle color="transparent">
                        <ak-icon type="more-vertical" />
                      </ak-button>
                    </template>
                    <ak-dropdown-item @click="editModal(item)">
                      Edit
                    </ak-dropdown-item>
                    <ak-dropdown-item
                      class="dropdown-danger"
                      @click="deleteContainer(item)"
                    >
                      Delete
                    </ak-dropdown-item>
                  </ak-dropdown>
                </ak-display-table-cell>
              </ak-display-table-row>
            </ak-display-table>
          </ak-box>
        </ak-basic-container>
      </div>
      <ak-fab
        color="secondary"
        position-x="center"
        position-y="bottom"
        @click="createModal"
      />
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { defineComponent, ref, watch, computed } from 'vue'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import BasicContainer from '../containers/BasicContainer.vue'
import ProjectSidebar from '../folders/ProjectFolderSidebar/ProjectSidebar.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Pack from '@akerolabs/akero-vue/src/components/pack/Pack.vue'
import PackItem from '@akerolabs/akero-vue/src/components/pack/PackItem.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Dropdown from '@akerolabs/akero-vue/src/components/dropdown/Dropdown.vue'
import DropdownItem from '@akerolabs/akero-vue/src/components/dropdown/DropdownItem.vue'
import Fab from '@akerolabs/akero-vue/src/components/fab/Fab.vue'
import { ModalProgrammatic } from '@akerolabs/akero-vue/src/components/modal'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import InstallContainer from './modals/InstallContainer.vue'
import ManageContainer from './modals/ManageContainer.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { error } from './utils'
import { fromNow } from '@akerolabs/akero-vue/src/filters/date'
export default defineComponent({
  name: 'AkPageManagerList',
  components: {
    [SidebarLayout.name]: SidebarLayout,
    [BasicContainer.name]: BasicContainer,
    [ProjectSidebar.name]: ProjectSidebar,
    [Form.name]: Form,
    [Loading.name]: Loading,
    [Pack.name]: Pack,
    [PackItem.name]: PackItem,
    [Field.name]: Field,
    [Input.name]: Input,
    [Box.name]: Box,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
    [Button.name]: Button,
    [Icon.name]: Icon,
    [Dropdown.name]: Dropdown,
    [DropdownItem.name]: DropdownItem,
    [Fab.name]: Fab,
    [ManageContainer.name]: ManageContainer,
  },
  props: {
    /**
     * @type {Object}
     * @description The active account (drilled in or users)
     */
    activeAccount: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The active agency (drilled in or users)
     */
    activeAgency: {
      default: () => ({}),
      type: Object,
    },
    /**
     * @type {Object}
     * @description The users profile data
     */
    userProfile: {
      default: () => ({}),
      type: Object,
    },
  },
  setup(props) {
    const isLoading = ref(true)
    const projectIdFilter = ref([])
    const searchFilter = ref('')
    const containers = ref([])
    /**
     * @type {Array}
     * @description Return a list of containers based on the current search
     */
    const filteredContainers = computed(() => {
      const search = searchFilter.value.toLowerCase()
      if (search && search.length > 0) {
        return containers.value.filter(({ title }) => {
          return title.toLowerCase().includes(search)
        })
      }
      return containers.value
    })

    /**
     * @description Watch the project filter so we can update the container list when it changes
     */
    watch(projectIdFilter, async (value) => {
      if (value.length > 0) {
        await fetchData()
      }
    })

    /**
     * @description Fetch containers from the api
     */
    const fetchData = async () => {
      let temp = []
      isLoading.value = true
      try {
        const { data } = await request.get('/containers', {
          params: {
            account: props.activeAccount._id,
            agency: props.activeAgency._id,
            overrideAccess: true,
            campaign: JSON.stringify({ $in: projectIdFilter.value }),
          },
        })
        temp = data.data
      } catch (ex) {
        error('Failed to fetch containers', ex)
      }
      containers.value = temp
      isLoading.value = false
    }

    /**
     * @param {Object} container
     * @description Open the install modal for the given container
     */
    const installModal = (container) => {
      ModalProgrammatic.open({
        component: InstallContainer,
        outerScroll: true,
        size: 'big',
        props: {
          container,
        },
      })
    }

    /**
     * @description Open the create container modal
     */
    const createModal = () => {
      ModalProgrammatic.open({
        component: ManageContainer,
        outerScroll: true,
        size: 'large',
        props: {},
        submitForm: async () => {
          await fetchData()
        },
      })
    }

    /**
     * @description Open the edit container modal
     */
    const editModal = (edit) => {
      ModalProgrammatic.open({
        component: ManageContainer,
        outerScroll: true,
        size: 'large',
        props: {
          edit: { ...edit },
        },
        submitForm: async () => {
          await fetchData()
        },
      })
    }

    /**
     * @description Open the delete confirm modal for the given container
     * @param {{_id: String, title: String}}
     */
    const deleteContainer = ({ _id, title }) => {
      DialogProgrammatic.prompt({
        title: 'Delete Container?',
        message: `
          <p>
            Permanently delete your Container ${title}
          </p>
          <small><strong>Type DELETE to confirm</strong></small>
        `,
        inputAttrs: {
          pattern: 'DELETE',
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: false,
        size: 'small',
        confirmText: 'Yes, Delete Container',
        cancelText: 'No, Keep Container',
        onConfirm: () => onConfirmDelete(_id),
      })
    }

    /**
     * @param {String} id
     * @description send api request to delete a container once it has been confimed
     */
    const onConfirmDelete = async (id) => {
      isLoading.value = true
      try {
        await request.delete(`/containers/${id}`)
        containers.value = containers.value.filter(({ _id }) => _id !== id)
      } catch (ex) {
        error('Failed to delete container', ex)
      }
      isLoading.value = false
    }

    return {
      isLoading,
      projectIdFilter,
      searchFilter,
      filteredContainers,
      fromNow,
      installModal,
      createModal,
      editModal,
      deleteContainer,
    }
  },
})
</script>

<style lang="scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
.dropdown-danger {
  color: $color-danger;
  &:hover {
    color: $color-danger !important;
  }
}
</style>
